import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material'
import React, { useEffect, useMemo, useState, useCallback, memo } from 'react'
import { IOrder, IOrderDetails } from '../../types/order';
import { getStringDate } from '../../utils/helpers/date';
import { CustomerContainer, CustomerTitle, CustomerWrapper, DeliveryAddress, DeliveryAddressContainer, DeliveryAddressTitle, OrderDetailDialogBodyContainer, OrderDetailDialogBodyWrapper, OrderDetailDialogContainer, OrderDetailDialogHeaderContainer, OrderDetailRow, OrderDetailRowData, OrderDetailRowLabel, PackingImage, PackingVideo, PaymentStatus, ProductImage, ProductName, TrackingDetailContainer, TrackingDetailData, TrackingDetailLabel, TrackingDetailRow, TrackingDetailTitle, TrackingDetailTitleContainer, TrackingWrapper } from './OrderDetailDialog.styled'
import {
  useMaterialReactTable,
  MaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { ICategory } from '../../types/category';
import { serverGetCategory } from '../../services/serverApi';
import CarousalDialog from '../CarousalDialog/CarousalDialog';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { formatNumber } from '../../utils/helpers/global';

interface Props {
  open: boolean;
  handleClose: () => void;
  orderData?: IOrder;
}

// Memoized component for better performance
const CurrencyDisplay = memo(({ amount }: { amount: number }) => (
  <Box sx={{ display: 'flex', alignItems: 'center' }}>
    <CurrencyRupeeIcon sx={{height: '15px', width: '15px'}}/>
    <div>{formatNumber(amount)}</div>
  </Box>
));

const OrderDetailDialog: React.FC<Props> = ({ open, handleClose, orderData }) => {
  const DELIVERY_CHARGE = 55;
  
  const [loading, setLoading] = useState<boolean>(false);
  const [categoryData, setCategoryData] = useState<ICategory[]>([]);
  const [openImageDialog, setOpenImageDialog] = useState<boolean>(false)
  const [imageDataPath, setImageDataPath] = useState<any>([])
  const [videoPath, setVideoPath] = useState<any>(undefined)
  const [showTrackingDetail, setShowTrackingDetail] = useState<boolean>(false)
  const [showUnloadingDetail, setShowUnloadingDetail] = useState<boolean>(false)

  // Memoized callbacks
  const handleToggleShowTracking = useCallback(() => {
    setShowTrackingDetail(prev => !prev);
  }, []);

  const handleToggleShowUnloadingDetail = useCallback(() => {
    setShowUnloadingDetail(prev => !prev);
  }, []);

  const handleOpenImageDialog = useCallback((data: any) => {
    setOpenImageDialog(true);
    setImageDataPath(data?.image);
    setVideoPath(data?.videoPath);
  }, []);

  const handleCloseImageDialog = useCallback(() => {
    setOpenImageDialog(false);
  }, []);

  // Memoized calculations
  const { totalAmount, totalQty } = useMemo(() => {
    const amount = orderData?.productDetails?.reduce((acc, curr) => {
      return acc + (typeof curr?.totalPrice === 'number' ? curr.totalPrice : 0);
    }, 0) ?? 0;

    const qty = orderData?.productDetails?.reduce((acc, curr) => {
      return acc + (typeof curr?.qty === 'number' ? curr.qty : 0);
    }, 0) ?? 0;

    return { totalAmount: amount, totalQty: qty };
  }, [orderData?.productDetails]);

  const deliveryCharge = useMemo(() => {
    return (orderData?.totalAmount ?? 0) - totalAmount;
  }, [orderData?.totalAmount, totalAmount]);

  // Memoized category lookup
  const getCategoryName = useCallback((categoryId: string) => {
    return categoryData?.find(item => item?._id === categoryId)?.name;
  }, [categoryData]);

  const columns = useMemo<MRT_ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'product.name',
        header: 'Name',
        Cell: ({ row }) => {
          return (
          <Box sx={{ display: 'flex', gap: '2ch', alignItems: 'center', rowGap: '5px' }}>
            <ProductImage src={row?.original?.product?.image[0]?.path} onClick={() => handleOpenImageDialog(row?.original?.product)}></ProductImage>
            <ProductName>{row?.original?.product?.name}</ProductName>
          </Box>
          )
        }
      },
      {
        accessorKey: 'product.categoryId',
        header: 'Category Name',
        Cell: ({ row }) => {
          return (
          <Box sx={{ display: 'flex', gap: '2ch', alignItems: 'center' }}>
            {getCategoryName(row?.original?.product?.categoryId)}
          </Box>
          )
        }
      },
      {
        accessorKey: 'product.code',
        header: 'Code'
      },
      {
        accessorKey: 'product.price',
        header: 'Price',
        Cell: ({ row }: any) => <CurrencyDisplay amount={Number(row?.original?.product?.price)} />
      },
      {
        accessorKey: 'qty',
        header: 'Qty',
        Cell: ({ row }: any) => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <div>{formatNumber(Number(row?.original?.qty))}</div>
          </Box>
        ),
        Footer: () => (
          <Stack>
            Total Qty:
            <Box color="warning.main">
              {formatNumber(Number(totalQty))}
            </Box>
          </Stack>
        ),
      },
      {
        accessorKey: 'totalPrice',
        header: 'Total Price',
        Cell: ({ row }: any) => <CurrencyDisplay amount={Number(row?.original?.totalPrice)} />,
        Footer: () => (
          <Stack spacing={0.5}>
            <Box>
              Subtotal:
              <Box color="info.main" sx={{ display: 'flex', alignItems: 'center' }}>
                <CurrencyRupeeIcon sx={{height: '15px', width: '15px'}}/>
                <div>{formatNumber(totalAmount)}</div>
              </Box>
            </Box>
            <Box>
              Delivery Charge:
              <Box color="info.main" sx={{ display: 'flex', alignItems: 'center' }}>
                <CurrencyRupeeIcon sx={{height: '15px', width: '15px'}}/>
                <div>{formatNumber(deliveryCharge)}</div>
              </Box>
            </Box>
            <Box>
              Total Amount:
              <Box color="warning.main" sx={{ display: 'flex', alignItems: 'center', fontWeight: 'bold' }}>
                <CurrencyRupeeIcon sx={{height: '15px', width: '15px'}}/>
                <div>{formatNumber(orderData?.totalAmount ?? 0)}</div>
              </Box>
            </Box>
          </Stack>
        ),
      },
    ],
    [totalAmount, totalQty, getCategoryName, handleOpenImageDialog, deliveryCharge, orderData?.totalAmount],
  );

  const table = useMaterialReactTable({
    columns,
    data: orderData?.productDetails ?? [],
    enableEditing: false,
    positionActionsColumn: 'last',
    enablePagination: false,
    enableBottomToolbar: false,
    state: { isLoading: loading },
    enableStickyHeader: true,
    muiTableContainerProps: { sx: { maxHeight: '65vh'} },
  });

  const getCategoryData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await serverGetCategory();
      setCategoryData(res?.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoized address string
  const deliveryAddress = useMemo(() => {
    const addr = orderData?.addressDetails;
    if (!addr) return '';
    return `${addr.addressFirst}, ${addr.area}, ${addr.city}, ${addr.state}, ${addr.country}, ${addr.pinCode}.`;
  }, [orderData?.addressDetails]);

  const hasTrackingDetails = useMemo(() => !!orderData?.trackingDetails?._id, [orderData?.trackingDetails]);
  const hasUnloadingDetails = useMemo(() => 
    !!(orderData?.unloadingDetails?.videoUrl || orderData?.unloadingDetails?.imageUrl), 
    [orderData?.unloadingDetails]
  );

  useEffect(() => {
    if (open) {
      getCategoryData();
    }
  }, [open, getCategoryData]);
  return (
    <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="lg"
    >
        <DialogTitle id="alert-dialog-title" bgcolor={'#0b3157'} color={'#ffffff'}>
          Order Details
        </DialogTitle>
        <DialogContent>
          <OrderDetailDialogContainer>
            <OrderDetailDialogHeaderContainer>
              <OrderDetailRow>
                <OrderDetailRowLabel>Payment Id:</OrderDetailRowLabel>
                <OrderDetailRowData>{orderData?.paymentId || 'COD'}</OrderDetailRowData>
              </OrderDetailRow>
              <OrderDetailRow>
                <OrderDetailRowLabel>Create Date:</OrderDetailRowLabel>
                <OrderDetailRowData>{getStringDate(orderData?.createdAt)}</OrderDetailRowData>
              </OrderDetailRow>
              <OrderDetailRow>
                <OrderDetailRowLabel>Total Amount:</OrderDetailRowLabel>
                <OrderDetailRowData><CurrencyRupeeIcon sx={{height: '15px', width: '15px'}}/>{orderData?.totalAmount}</OrderDetailRowData>
              </OrderDetailRow>

              <PaymentStatus>{orderData?.status}</PaymentStatus>
            </OrderDetailDialogHeaderContainer>
            <OrderDetailDialogBodyWrapper>
              <OrderDetailDialogBodyContainer>
                <DeliveryAddressContainer>
                  <DeliveryAddressTitle>Delivery Address</DeliveryAddressTitle>
                  <DeliveryAddress>{deliveryAddress}</DeliveryAddress>
                </DeliveryAddressContainer>
                <CustomerContainer>
                  <CustomerTitle>Customer</CustomerTitle>
                  <CustomerWrapper>
                    <OrderDetailRow>
                      <OrderDetailRowLabel>Name:</OrderDetailRowLabel>
                      <OrderDetailRowData>{orderData?.userData?.name}</OrderDetailRowData>
                    </OrderDetailRow>
                    <OrderDetailRow>
                      <OrderDetailRowLabel>Email:</OrderDetailRowLabel>
                      <OrderDetailRowData>{orderData?.userData?.email}</OrderDetailRowData>
                    </OrderDetailRow>
                    <OrderDetailRow>
                      <OrderDetailRowLabel>Phone:</OrderDetailRowLabel>
                      <OrderDetailRowData>{orderData?.userData?.mobileNumber}</OrderDetailRowData>
                    </OrderDetailRow>
                  </CustomerWrapper>
                </CustomerContainer>
              </OrderDetailDialogBodyContainer>
              {hasTrackingDetails && (
                <TrackingWrapper>
                  <TrackingDetailTitleContainer onClick={() => handleToggleShowTracking()}>
                    <TrackingDetailTitle>Show Tracking Detail</TrackingDetailTitle>
                    {showTrackingDetail ? (
                      <KeyboardArrowUpIcon sx={{color: '#ffffff'}}/>
                    ) : (
                      <KeyboardArrowDownIcon sx={{color: '#ffffff'}}/>
                    )}
                  </TrackingDetailTitleContainer>
                  {showTrackingDetail && (
                    <TrackingDetailContainer>
                        <TrackingDetailRow>
                          <TrackingDetailLabel>Track Number</TrackingDetailLabel>
                          <TrackingDetailData>{orderData?.trackingDetails?.trackingId}</TrackingDetailData>
                        </TrackingDetailRow>
                        {orderData?.trackingDetails?.video && (
                          <TrackingDetailRow>
                            <TrackingDetailLabel>Packing Video</TrackingDetailLabel>
                            <PackingVideo src={orderData?.trackingDetails?.video} controls></PackingVideo>
                          </TrackingDetailRow>
                        )}
                    </TrackingDetailContainer>
                  )}
                </TrackingWrapper>
              )}
              {hasUnloadingDetails && (
                <TrackingWrapper>
                  <TrackingDetailTitleContainer onClick={handleToggleShowUnloadingDetail}>
                    <TrackingDetailTitle>Show Unloading Detail</TrackingDetailTitle>
                    {showUnloadingDetail ? (
                      <KeyboardArrowUpIcon sx={{color: '#ffffff'}}/>
                    ) : (
                      <KeyboardArrowDownIcon sx={{color: '#ffffff'}}/>
                    )}
                  </TrackingDetailTitleContainer>
                  {showUnloadingDetail && (
                    <TrackingDetailContainer>
                      {orderData?.unloadingDetails?.imageUrl && (
                        <TrackingDetailRow>
                          <TrackingDetailLabel>Packing Image</TrackingDetailLabel>
                          <PackingImage src={orderData?.unloadingDetails?.imageUrl}></PackingImage>
                        </TrackingDetailRow>
                      )}
                      {orderData?.unloadingDetails?.videoUrl && (
                        <TrackingDetailRow>
                          <TrackingDetailLabel>Packing Video</TrackingDetailLabel>
                          <PackingVideo src={orderData?.unloadingDetails?.videoUrl} controls></PackingVideo>
                        </TrackingDetailRow>
                      )}
                    </TrackingDetailContainer>
                  )}
                </TrackingWrapper>
              )}
              <MaterialReactTable table={table} />
            </OrderDetailDialogBodyWrapper>
          </OrderDetailDialogContainer>
        </DialogContent>
        {/* <DialogActions>
            <Button onClick={handleClose} 
                sx={{
                    background: '#1876d1',
                    color: '#ffffff',
                    '&:hover': { 
                        background: '#1876d1',
                    },
                }}
                disabled={loading}
            >
                Cancel
            </Button>
            <Button onClick={() => handleSuccess()} 
                sx={{
                    background: '#d32f2f', 
                    color: '#ffffff',
                    '&:hover': { 
                        background: '#d32f2f',
                    },
                }}
                disabled={loading}
            >
                Ok
            </Button>
        </DialogActions> */}

      {openImageDialog && (
        <CarousalDialog 
          open={openImageDialog} 
          handleClose={handleCloseImageDialog} 
          imageDataPath={imageDataPath} 
          videoPath={videoPath} 
        />
      )}
    </Dialog>
  )
}

export default memo(OrderDetailDialog)